// src/auth/TokenManager.js
import { auth } from '../firebase';

class TokenManager {
  constructor() {
    this.listeners = new Set();
    this.SESSION_DURATION = 60 * 60 * 1000;     // 1 hour
    this.SESSION_WARNING_TIME = 5 * 60 * 1000;  // warn 5 minutes before expiry
    this.warningTimer = null;
    this.expireTimer = null;
    this.sessionStartTime = null;
    this.onWarning = null;
    this.onExpire = null;
    this._visibilityHandler = null;
    this._lastActivityReset = null;
  }

  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(tokenInfo) {
    this.listeners.forEach(listener => {
      try {
        listener(tokenInfo);
      } catch (error) {
        console.error('Error in token listener:', error);
      }
    });
  }

  async getToken(forceRefresh = false) {
    const user = auth.currentUser;
    if (!user) {
      this.notifyListeners(null);
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const tokenInfo = {
        token,
        expirationTime: decodedToken.exp * 1000,
        claims: decodedToken
      };
      this.notifyListeners(tokenInfo);
      return token;
    } catch (error) {
      console.error('Token fetch failed:', error);
      this.notifyListeners(null);
      throw error;
    }
  }

  startSessionTimers(onWarning, onExpire) {
    this.clearSessionTimers();
    this.sessionStartTime = Date.now();
    this.onWarning = onWarning;
    this.onExpire = onExpire;

    this.warningTimer = setTimeout(onWarning, this.SESSION_DURATION - this.SESSION_WARNING_TIME);
    this.expireTimer = setTimeout(onExpire, this.SESSION_DURATION);

    this._visibilityHandler = () => this._handleVisibilityChange();
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  // Recalculates and resets timers when the tab becomes visible again,
  // correcting for time that passed while the tab was backgrounded or the device was asleep.
  _handleVisibilityChange() {
    if (document.visibilityState !== 'visible' || !this.sessionStartTime) return;

    const elapsed = Date.now() - this.sessionStartTime;
    const remaining = this.SESSION_DURATION - elapsed;

    clearTimeout(this.warningTimer);
    clearTimeout(this.expireTimer);

    if (remaining <= 0) {
      this.warningTimer = null;
      this.expireTimer = null;
      if (this.onExpire) this.onExpire();
      return;
    }

    const msUntilWarning = remaining - this.SESSION_WARNING_TIME;
    if (msUntilWarning > 0) {
      this.warningTimer = setTimeout(this.onWarning, msUntilWarning);
    } else {
      this.warningTimer = null;
      if (this.onWarning) this.onWarning();
    }

    this.expireTimer = setTimeout(this.onExpire, remaining);
  }

  restartSessionTimers() {
    if (this.onWarning !== null || this.onExpire !== null) {
      this.startSessionTimers(this.onWarning || (() => {}), this.onExpire || (() => {}));
    }
  }

  // Called by the API client on every successful response. Resets the session
  // timer so that 1 hour of inactivity — not 1 hour from login — triggers logout.
  // Throttled to at most once per minute to avoid thrashing on rapid API calls.
  resetOnActivity() {
    if (!this.sessionStartTime) return;
    if (this.getSessionTimeRemaining() <= this.SESSION_WARNING_TIME) return;

    const now = Date.now();
    if (this._lastActivityReset && (now - this._lastActivityReset) < 60000) return;

    this._lastActivityReset = now;
    this.restartSessionTimers();
  }

  clearSessionTimers() {
    clearTimeout(this.warningTimer);
    clearTimeout(this.expireTimer);
    this.warningTimer = null;
    this.expireTimer = null;
    this.sessionStartTime = null;
    this.onWarning = null;
    this.onExpire = null;
    this._lastActivityReset = null;
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }

  getSessionTimeRemaining() {
    if (!this.sessionStartTime) return 0;
    const elapsed = Date.now() - this.sessionStartTime;
    return Math.max(0, this.SESSION_DURATION - elapsed);
  }

  isSessionExpiringSoon() {
    const user = auth.currentUser;
    if (!user) return false;
    
    try {
      const decodedToken = JSON.parse(atob(user.stsTokenManager.accessToken.split('.')[1]));
      const expirationTime = decodedToken.exp * 1000;
      return (expirationTime - Date.now()) <= this.SESSION_WARNING_TIME;
    } catch (error) {
      return false;
    }
  }

  getSecondsUntilExpiry() {
    const user = auth.currentUser;
    if (!user) return 0;

    try {
      const decodedToken = JSON.parse(atob(user.stsTokenManager.accessToken.split('.')[1]));
      const expirationTime = decodedToken.exp * 1000;
      return Math.max(0, Math.floor((expirationTime - Date.now()) / 1000));
    } catch (error) {
      return 0;
    }
  }
}

export const tokenManager = new TokenManager();
export default tokenManager;